import React, { FC, useEffect, useRef, useState } from 'react'
import { MobileSearchAssistant } from '@mobileTypes'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'

import { useFetchObjectDataRecordFormDropDownDefaultByFieldsMutation } from '@redux/api'

import { FIELD_VALUE_TYPE } from '@constants'
import { AutocompleteOption } from '@types'

import { MobilePhoneFrame } from './frames/MobilePhoneFrame'

interface MobileSearchAssistantPreviewProps {
  searchAssistant: MobileSearchAssistant
}

export const MobileSearchAssistantPreview: FC<MobileSearchAssistantPreviewProps> = ({
  searchAssistant,
}) => {
  const [fetchData] = useFetchObjectDataRecordFormDropDownDefaultByFieldsMutation()
  const [optionsByFilterId, setOptionsByFilterId] = useState<Record<string, AutocompleteOption[]>>(
    {}
  )
  const [loadingByFilterId, setLoadingByFilterId] = useState<Record<string, boolean>>({})
  const [valuesByFilterId, setValuesByFilterId] = useState<Record<string, any>>({})
  const [pageByFilterId, setPageByFilterId] = useState<Record<string, number>>({})
  const [hasMoreByFilterId, setHasMoreByFilterId] = useState<Record<string, boolean>>({})
  const [inputValueByFilterId, setInputValueByFilterId] = useState<Record<string, string>>({})

  const PAGE_SIZE = 50
  const DEBOUNCE_DELAY = 300

  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({})
  const activeSearchByFilterId = useRef<Record<string, string>>({})

  // Сброс состояния при смене набора фильтров / конфигурации
  useEffect(() => {
    Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer))
    debounceTimers.current = {}
    activeSearchByFilterId.current = {}

    setOptionsByFilterId({})
    setLoadingByFilterId({})
    setValuesByFilterId({})
    setPageByFilterId({})
    setHasMoreByFilterId({})
    setInputValueByFilterId({})
  }, [searchAssistant.filters])

  // Очистка таймеров при размонтировании
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer))
    }
  }, [])

  const loadFilterData = (filter: any, page = 0, append = false, searchText?: string) => {
    // valuePathArray содержит путь к полю, последний элемент имеет model - это и есть objectCode для загрузки
    const valuePathArray = filter.config?.valuePathArray || filter.value?.valuePathArray || []
    // pathArray содержит путь к отображаемому полю, последний элемент имеет field - это поле для отображения
    const pathArray = filter.config?.pathArray || filter.value?.pathArray || []

    console.log('filter', filter)

    const objectCode =
      valuePathArray.length > 0 ? valuePathArray[valuePathArray.length - 1]?.model : null
    const objectValue = filter.config?.objectValueString

    if (!objectCode) return

    // Формируем фильтр для поиска
    const filterQuery = searchText ? `${objectValue}==*${searchText}*` : undefined

    setLoadingByFilterId(prev => ({ ...prev, [filter.id]: true }))
    fetchData({
      objectCode,
      fields: ['internal_id', objectValue],
      page,
      size: PAGE_SIZE,
      ...(filterQuery && { filter: filterQuery }),
    })
      .unwrap()
      .then(result => {
        const newOptions: AutocompleteOption[] = (result.data || []).map((item: any) => ({
          id: item._id || item.internal_id || '',
          label: item[objectValue] || String(item._id || item.internal_id || ''),
        }))

        setOptionsByFilterId(prev => ({
          ...prev,
          [filter.id]: append ? [...(prev[filter.id] || []), ...newOptions] : newOptions,
        }))

        setPageByFilterId(prev => ({ ...prev, [filter.id]: page }))
        setHasMoreByFilterId(prev => ({
          ...prev,
          [filter.id]: newOptions.length === PAGE_SIZE,
        }))
      })
      .catch(() => {
        setOptionsByFilterId(prev => ({ ...prev, [filter.id]: [] }))
        setHasMoreByFilterId(prev => ({ ...prev, [filter.id]: false }))
      })
      .finally(() => {
        setLoadingByFilterId(prev => ({ ...prev, [filter.id]: false }))
      })
  }

  const handleFilterChange = (filterId: string, value: any) => {
    setValuesByFilterId(prev => ({ ...prev, [filterId]: value }))
  }

  const handleAutocompleteOpen = (filter: any) => {
    // Загружаем данные только если еще не загружены
    if (!optionsByFilterId[filter.id] && !loadingByFilterId[filter.id]) {
      loadFilterData(filter, 0, false)
    }
  }

  const handleAutocompleteScroll = (filter: any) => (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget
    const isBottom =
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10

    if (isBottom && !loadingByFilterId[filter.id] && hasMoreByFilterId[filter.id]) {
      const nextPage = (pageByFilterId[filter.id] || 0) + 1
      // Используем активный поиск, а не текущий inputValue
      const searchText = activeSearchByFilterId.current[filter.id] || ''
      loadFilterData(filter, nextPage, true, searchText)
    }
  }

  const handleInputChange =
    (filter: any) => (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      setInputValueByFilterId(prev => ({ ...prev, [filter.id]: newInputValue }))

      // Делаем запрос только при реальном вводе текста пользователем, а не при выборе элемента
      if (reason !== 'input') {
        // При выборе элемента или сбросе очищаем активный поиск
        if (reason === 'reset' || reason === 'clear') {
          activeSearchByFilterId.current[filter.id] = ''
        }

        return
      }

      // Очищаем предыдущий таймер
      if (debounceTimers.current[filter.id]) {
        clearTimeout(debounceTimers.current[filter.id])
      }

      // Устанавливаем новый таймер
      debounceTimers.current[filter.id] = setTimeout(() => {
        // Сохраняем активный поиск
        activeSearchByFilterId.current[filter.id] = newInputValue
        // Сбрасываем опции и загружаем с новым фильтром
        setOptionsByFilterId(prev => ({ ...prev, [filter.id]: [] }))
        setPageByFilterId(prev => ({ ...prev, [filter.id]: 0 }))
        loadFilterData(filter, 0, false, newInputValue)
      }, DEBOUNCE_DELAY)
    }

  const renderFilter = (filter: any) => {
    // Поддерживаем оба варианта структуры данных: config и value
    const valueType = filter.config?.valueType || filter.value?.valuePathArray?.[0]?.valueType
    const valuePathArray = filter.config?.valuePathArray || filter.value?.valuePathArray || []
    const isObjectLinkType =
      valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK ||
      valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK
    const objectCode =
      valuePathArray.length > 0 ? valuePathArray[valuePathArray.length - 1]?.model : null
    const currentValue = valuesByFilterId[filter.id]

    switch (filter.type) {
      case 'SEARCHBAR':
        if (filter.config?.asCheckbox) {
          return (
            <Box key={filter.id} sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size='medium'
                    checked={Boolean(currentValue)}
                    onChange={e => handleFilterChange(filter.id, e.target.checked)}
                  />
                }
                label={filter.label}
              />
            </Box>
          )
        }

        if (isObjectLinkType && objectCode) {
          const options = optionsByFilterId[filter.id] || []
          const isLoading = loadingByFilterId[filter.id] || false
          const hasMore = hasMoreByFilterId[filter.id] || false

          return (
            <Autocomplete
              key={filter.id}
              fullWidth
              size='small'
              options={options}
              loading={isLoading}
              value={currentValue || null}
              inputValue={inputValueByFilterId[filter.id] || ''}
              getOptionLabel={option => option.label || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => handleFilterChange(filter.id, newValue)}
              onInputChange={handleInputChange(filter)}
              onOpen={() => handleAutocompleteOpen(filter)}
              ListboxProps={{
                onScroll: handleAutocompleteScroll(filter),
                style: { maxHeight: '300px' },
                role: 'list-box',
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={filter.label}
                  placeholder={filter.config?.placeholder || 'Select...'}
                />
              )}
               renderOption={(props, option, state) => {
                 const isLastItem = state.index === options.length - 1

                 return (
                   <>
                     <li {...props} key={option.id}>
                       {option.label}
                     </li>
                     {isLastItem && hasMore && isLoading && (
                       <Box
                         component='li'
                         sx={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           py: 1,
                           width: '100%',
                           listStyle: 'none',
                         }}
                       >
                         <CircularProgress size={12} />
                       </Box>
                     )}
                   </>
                 )
               }}
              sx={{ mb: 2 }}
            />
          )
        }

        return (
          <TextField
            key={filter.id}
            fullWidth
            size='small'
            label={filter.label}
            placeholder={filter.config?.placeholder || 'Search...'}
            value={currentValue || ''}
            onChange={e => handleFilterChange(filter.id, e.target.value)}
            sx={{ mb: 2 }}
          />
        )

      case 'SELECT':
        return (
          <TextField
            key={filter.id}
            fullWidth
            size='small'
            select
            label={filter.label}
            value={currentValue || ''}
            onChange={e => handleFilterChange(filter.id, e.target.value)}
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
          >
            <option value=''>Select...</option>
            {(filter.config?.options || []).map((opt: any, idx: number) => (
              <option key={idx} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </TextField>
        )

      case 'MULTISELECT':
        const selectedValues = currentValue || []

        return (
          <Box key={filter.id} sx={{ mb: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
              {filter.label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(filter.config?.options || []).map((opt: any, idx: number) => {
                const isSelected = selectedValues.includes(opt.value)

                return (
                  <Chip
                    key={idx}
                    label={opt.label}
                    size='small'
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => {
                      const newValues = isSelected
                        ? selectedValues.filter((v: any) => v !== opt.value)
                        : [...selectedValues, opt.value]
                      handleFilterChange(filter.id, newValues)
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        )

      case 'TOGGLE_BUTTONS':
        return (
          <Box key={filter.id} sx={{ mb: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
              {filter.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {(filter.config?.options || []).map((opt: any, idx: number) => {
                const isSelected = currentValue === opt.value

                return (
                  <Button
                    key={idx}
                    variant={isSelected ? 'contained' : 'outlined'}
                    size='small'
                    onClick={() => handleFilterChange(filter.id, opt.value)}
                  >
                    {opt.label}
                  </Button>
                )
              })}
            </Box>
          </Box>
        )

      case 'DAY_OFFSET':
        return (
          <Box key={filter.id} sx={{ mb: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
              {filter.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {(filter.config?.dayOffsets || []).map((offset: number, idx: number) => {
                const isSelected = currentValue === offset

                return (
                  <Button
                    key={idx}
                    variant={isSelected ? 'contained' : 'outlined'}
                    size='small'
                    onClick={() => handleFilterChange(filter.id, offset)}
                  >
                    {offset >= 0 ? `+${offset}` : offset}
                  </Button>
                )
              })}
            </Box>
          </Box>
        )

      case 'DATE_RANGE':
        const dateRangeValue = currentValue || { from: '', to: '' }

        return (
          <Box key={filter.id} sx={{ mb: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
              {filter.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size='small'
                type='date'
                label='From'
                value={dateRangeValue.from || ''}
                onChange={e =>
                  handleFilterChange(filter.id, { ...dateRangeValue, from: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size='small'
                type='date'
                label='To'
                value={dateRangeValue.to || ''}
                onChange={e =>
                  handleFilterChange(filter.id, { ...dateRangeValue, to: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        )

      case 'CHECKBOX':
        return (
          <Box key={filter.id} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size='medium'
                  checked={currentValue || false}
                  onChange={e => handleFilterChange(filter.id, e.target.checked)}
                />
              }
              label={filter.label}
            />
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <MobilePhoneFrame>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: 'background.paper',
        }}
      >
        {/* Синяя шапка с заголовком */}
        <Box
          sx={{
            bgcolor: '#2f5a9c',
            color: 'white',
            py: 2,
            px: 2,
            textAlign: 'center',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Typography variant='h6' sx={{ fontWeight: 500, color: 'white' }}>
            {searchAssistant.title || 'Редактировать приложение'}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
            bgcolor: 'white',
          }}
        >
          {searchAssistant.filters && searchAssistant.filters.length > 0 ? (
            <>{searchAssistant.filters.map(filter => renderFilter(filter))}</>
          ) : (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ textAlign: 'center', py: 4 }}
            >
              No filters configured
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
          }}
        >
          <Button
            variant='outlined'
            fullWidth
            sx={{
              borderRadius: '30px',
              textTransform: 'none',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 400,
              borderColor: '#5568c5',
              color: '#5568c5',
              borderWidth: '1.5px',
              '&:hover': {
                borderColor: '#5568c5',
                bgcolor: 'rgba(85, 104, 197, 0.04)',
                borderWidth: '1.5px',
              },
            }}
          >
            Отмена
          </Button>
          <Button
            variant='contained'
            fullWidth
            sx={{
              borderRadius: '30px',
              textTransform: 'none',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
              bgcolor: '#3a89ff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#3a89ff',
                boxShadow: 'none',
              },
            }}
          >
            Сохранить
          </Button>
        </Box>
      </Box>
    </MobilePhoneFrame>
  )
}
